import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { useIsGlobalAdmin } from '@/hooks/useIsGlobalAdmin'
import { formatDuration } from '@/utils/time'
import type { SessionAttemptRecord, SessionAttemptResult } from '../types'

const attemptResultColors: Record<SessionAttemptResult, string> = {
  success: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  cancelled: 'bg-gray-100 text-gray-600',
  escalated: 'bg-orange-100 text-orange-800',
}

interface SessionAttemptsSubRowsTableProps {
  subRows: SessionAttemptRecord[]
  sessionId: string
  searchParamsString: string
  onAttemptClick: (targetUrl: string) => void
}

export function SessionAttemptsSubRowsTable({
  subRows,
  sessionId,
  onAttemptClick,
}: SessionAttemptsSubRowsTableProps) {
  // Dollar cost is GlobalAdmin-only; the API also nulls token_cost for
  // other roles.
  const isGlobalAdmin = useIsGlobalAdmin()
  if (!subRows.length) {
    return (
      <div className="text-sm text-muted-foreground py-2">
        No attempts found for session{' '}
        <span className="font-mono">{sessionId}</span>.
      </div>
    )
  }

  return (
    <div className="py-2 pl-8">
      <div className="overflow-x-auto rounded border bg-background">
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 text-xs">Attempt ID</TableHead>
              <TableHead className="h-8 text-xs">Result</TableHead>
              <TableHead className="h-8 text-xs">Started</TableHead>
              <TableHead className="h-8 text-xs">Completed</TableHead>
              <TableHead className="h-8 text-xs text-right">Duration</TableHead>
              <TableHead className="h-8 text-xs text-right">Tokens</TableHead>
              {isGlobalAdmin && (
                <TableHead className="h-8 text-xs text-right">Cost</TableHead>
              )}
              <TableHead className="h-8 text-xs">Error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {subRows.map((attempt) => {
              const targetUrl = `/sessions/${sessionId}#${attempt.id}`
              return (
                <TableRow key={attempt.id} className="hover:bg-muted/40">
                  <TableCell className="py-2">
                    <div className="group inline-flex items-center gap-1">
                      <Button
                        variant="link"
                        className="p-0 h-auto font-mono text-xs truncate"
                        onClick={() => onAttemptClick(targetUrl)}
                        title={attempt.id}>
                        {attempt.id}
                      </Button>
                      <CopyButton
                        content={attempt.id}
                        label="Attempt ID"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  </TableCell>
                  <TableCell className="py-2">
                    {attempt.result ? (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          attemptResultColors[attempt.result]
                        }`}>
                        {attempt.result}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {attempt.started_at
                      ? new Date(attempt.started_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                      : '-'}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-muted-foreground whitespace-nowrap">
                    {attempt.completed_at
                      ? new Date(attempt.completed_at).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                      : '-'}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-right whitespace-nowrap">
                    {formatDuration(attempt.started_at, attempt.completed_at)}
                  </TableCell>
                  <TableCell className="py-2 text-xs text-right whitespace-nowrap">
                    {attempt.token_usage != null
                      ? attempt.token_usage.toLocaleString('en-US')
                      : '-'}
                  </TableCell>
                  {isGlobalAdmin && (
                    <TableCell className="py-2 text-xs text-right whitespace-nowrap">
                      {attempt.token_cost != null
                        ? `$${attempt.token_cost.toFixed(4)}`
                        : '-'}
                    </TableCell>
                  )}
                  <TableCell className="py-2 text-xs">
                    {attempt.error ? (
                      <span className="text-red-600 break-words">
                        {attempt.error}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
